import {
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import i18n from 'i18next';
import styles from './calendarStyle.module.scss';
import { Language } from 'src/interface';

const daysLimit = (year: number) => [
  31,
  year % 4 ? 28 : 29,
  31,
  30,
  31,
  30,
  31,
  31,
  30,
  31,
  30,
  31,
];

export const ruWeekDays = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
export const enWeekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const ruMonths = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
];

export const enMonths = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export type InputStatus = 'valid' | 'invalid' | null;

export type CalendarDateOptions = {
  isClicked?: boolean;
  isMovable?: boolean;
  inputStatus?: InputStatus;
};

export class CalendarDate {
  _date: Date | null = null;
  _isClicked: boolean = true;
  _isMovable: boolean = true;
  _inputStatus: InputStatus = null;

  constructor(date?: Date | string | null, options?: CalendarDateOptions) {
    if (options) {
      this._isClicked = options.isClicked ?? true;
      this._isMovable = options.isMovable ?? true;
      this._inputStatus = options.inputStatus ?? null;
    }

    if (date === null) {
      return;
    }

    if (date && date instanceof Date) {
      this._date = new Date(date);
      return;
    }

    if (typeof date === 'string' && date.length === 10) {
      this._date = new Date(
        Number(date.slice(0, 4)),
        Number(date.slice(5, 7)) - 1,
        Number(date.slice(8, 10)),
        0,
        0,
        0,
        0,
      );
      return;
    }

    this._date = new Date();
    this._date.setHours(0, 0, 0, 0);
  }

  get date() {
    return this._date;
  }

  set date(value: Date | null) {
    this._date = value;
  }

  get day() {
    return this.date?.getDate() || 0;
  }

  set day(value: number) {
    const limit = daysLimit(this.year)[this.month - 1];
    if (value < 1 || value > limit) {
      console.error('Limit');
      return;
    }

    this.date?.setDate(value);
  }

  setDay(value: number) {
    this.date?.setDate(this.day + value);
    return this;
  }

  setFirstDay() {
    this.day = 1;
    return this;
  }

  setLastDay() {
    this.day = daysLimit(this.year)[this.month - 1] || 0;
    return this;
  }

  get month() {
    return this._date ? this._date.getMonth() + 1 : 0;
  }

  set month(value: number) {
    if (value < 1 || value > 12) {
      console.error('Limit');
      return;
    }

    this._date?.setMonth(value - 1);
  }

  get year() {
    return this._date?.getFullYear() || 0;
  }

  set year(value: number) {
    this._date?.setFullYear(value < 100 ? 2000 + value : value);
  }

  get weekDay() {
    if (!this._date) {
      return 0;
    }

    const day = this._date.getDay();

    if (i18n.language === Language.EN) {
      return day;
    }

    return (day + 6) % 7;
  }

  get time() {
    return this._date?.getTime() || 0;
  }

  get localString() {
    return (
      this._date?.toLocaleDateString(
        i18n.language === Language.EN ? 'en-CA' : 'ru-RU',
      ) || ''
    );
  }

  get format() {
    return this._date?.toLocaleDateString('en-CA') || '';
  }

  get isClicked() {
    return this._isClicked;
  }

  setClicked(value: boolean) {
    this._isClicked = value;
    return this;
  }

  get isMovable() {
    return Boolean(this._date) && this._isMovable;
  }

  setMovable(value: boolean) {
    this._isMovable = value;
    return this;
  }

  get inputStatus() {
    return this._inputStatus;
  }

  isEqual(d: CalendarDate) {
    return (
      this.time === d.time &&
      this.isClicked === d.isClicked &&
      this.isMovable === d.isMovable &&
      this.inputStatus === d.inputStatus
    );
  }
}

export const useCalendarPosition = () => {
  const [mode, setMode] = useState<'hide' | 'init' | 'view'>('hide');
  const [containerPosition, setContainerPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const btnRef = useRef<HTMLDivElement | null>(null);
  const portalRef = useRef<HTMLDivElement | null>(null);

  const isView = useMemo(() => mode === 'view', [mode]);

  const toForceHide = useCallback((event?: MouseEvent<HTMLDivElement>) => {
    event?.stopPropagation();
    setMode('hide');
  }, []);

  const onBtnClick = useCallback((event?: MouseEvent<HTMLDivElement>) => {
    event?.stopPropagation();
    const rect = btnRef.current?.getBoundingClientRect();
    if (rect) {
      setContainerPosition({
        left: rect.left - 5,
        top: rect.top - 5,
        width: rect.width,
      });
      setMode('init');
    }
  }, []);

  const scrollElRef = useRef<HTMLDivElement | null>(null);
  const toHide = useCallback(() => {
    if (
      !scrollElRef.current ||
      scrollElRef.current.getBoundingClientRect().top === 0
    ) {
      toForceHide();
    } else {
      scrollElRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start',
      });
      setTimeout(() => {
        toForceHide();
      }, 200);
    }
  }, [toForceHide]);

  useEffect(() => {
    if (!portalRef.current || !btnRef.current || mode === 'hide') {
      return;
    }

    const observer = new ResizeObserver(() => {
      const rect = btnRef.current?.getBoundingClientRect();
      if (rect) {
        setContainerPosition({
          left: rect.left - 5,
          top: rect.top - 5,
          width: rect.width,
        });
      }
    });

    observer.observe(portalRef.current);

    return () => {
      observer.disconnect();
    };
  }, [mode]);

  useEffect(() => {
    if (mode === 'init') {
      setMode('view');
    }

    return () => {
      if (mode === 'view') {
        setTimeout(() => {
          setContainerPosition(null);
        }, 200);
      }
    };
  }, [mode]);

  return {
    isView,
    toHide,
    btnRef,
    portalRef,
    scrollElRef,
    onBtnClick,
    containerPosition,
  };
};

export const getSelectedClassName = (
  isRange: boolean,
  current: number | undefined,
  start: number | undefined,
  end: number | undefined,
): string | boolean | null => {
  if (!current) {
    return null;
  }

  if (!isRange) {
    return current === start && styles.selectedSingle;
  }

  if (!start && !end) {
    return null;
  }

  if (current === end) {
    return start === end ? styles.selectedSingle : styles.selectedRangeEnd;
  }

  if (current === start) {
    return end ? styles.selectedRangeStart : styles.selectedSingle;
  }

  if (start && end && current > start && current < end) {
    return styles.selectedRange;
  }

  return null;
};

export const parseDate = (parts: string[], options: CalendarDateOptions) => {
  let day: number;
  let month: number;
  let year: number;
  switch (i18n.language as Language) {
    case Language.EN: {
      year = Number(parts[0]);
      month = Number(parts[1]);
      day = Number(parts[2]);
      break;
    }
    case Language.RU: {
      day = Number(parts[0]);
      month = Number(parts[1]);
      year = Number(parts[2]);
    }
  }

  if (year > 0 && year < 100) {
    year += 2000;
  }

  if (
    day < 1 ||
    day > daysLimit(year)[month - 1] ||
    month < 1 ||
    month > 12 ||
    year < 1900 ||
    year > 2100
  ) {
    return new CalendarDate(null, {
      ...options,
      inputStatus: 'invalid',
    });
  }

  return new CalendarDate(new Date(year, month - 1, day, 0, 0, 0, 0), {
    ...options,
    inputStatus: 'valid',
  });
};

export const parseDateSingle = (
  value: string,
  withoutClear: boolean,
): CalendarDate => {
  const options = {
    isClicked: false,
    isMovable: true,
  };

  if (value.trim() === '') {
    return new CalendarDate(null, {
      ...options,
      inputStatus: withoutClear ? 'invalid' : 'valid',
    });
  }

  const matches = value.match(/\d+/g);
  const parts = matches ? [...matches] : [];
  if (parts.length !== 3) {
    return new CalendarDate(null, {
      ...options,
      inputStatus: 'invalid',
    });
  }

  return parseDate(parts, options);
};

export type DateRange = {
  start: CalendarDate | null;
  end: CalendarDate | null;
  reverse?: boolean;
};

export type InputDateRange = {
  start: CalendarDate;
  end: CalendarDate;
  reverse?: boolean;
};

export const toInputDateRange = (range: DateRange): InputDateRange => ({
  start: range.start || new CalendarDate(null),
  end: range.end || new CalendarDate(null),
  reverse: range.reverse,
});

export const toDateRange = (inputRange: InputDateRange): DateRange => {
  const reverse =
    inputRange.start.date &&
    inputRange.end.date &&
    inputRange.start.time > inputRange.end.time;
  const start = inputRange.start.date
    ? new CalendarDate(inputRange.start.date)
    : null;
  const end = inputRange.end.date
    ? new CalendarDate(inputRange.end.date)
    : null;

  if (reverse) {
    return {
      start: end,
      end: start,
    };
  }

  return { start, end };
};

export const parseDateRange = (
  value: string,
  prevRange: InputDateRange,
  withoutClear: boolean,
): InputDateRange => {
  const options = {
    isClicked: false,
    isMovable: false,
  };
  const matches = value.match(/\d+/g);
  const parts = matches ? [...matches] : [];

  if (value.trim() === '' || parts.length < 3) {
    return {
      start: new CalendarDate(null, {
        ...options,
        inputStatus: withoutClear || parts.length ? 'invalid' : 'valid',
      }),
      end: new CalendarDate(null, {
        ...options,
        inputStatus: withoutClear || parts.length ? 'invalid' : 'valid',
      }),
      reverse: false,
    };
  }

  const start = parseDate(parts.slice(0, 3), options);
  const end = parseDate(parts.slice(-3), options);
  const { start: prevStart, end: prevEnd, reverse: prevReverse } = prevRange;

  const nullDate = new CalendarDate(null, {
    ...options,
    inputStatus: 'invalid',
  });

  if (parts.length === 3) {
    if (start.date && start.time !== prevStart.time) {
      start.setMovable(true);
    }

    return {
      start,
      end: nullDate,
      reverse: false,
    };
  }

  const reverse = start.date && end.date ? start.time > end.time : prevReverse;

  // Correct length of parts or only one of new date is valid
  if (parts.length === 6 || Boolean(start.date) !== Boolean(end.date)) {
    if (start.date && start.time !== prevStart.time) {
      start.setMovable(true);
    } else if (end.date && end.time !== prevEnd.time) {
      end.setMovable(true);
    }
    return { start, end, reverse };
  }

  // all new dates are invalid
  if (!start.date || !end.date) {
    return { start, end, reverse: false };
  }

  // all new date are valid
  // all prev dates are invalid
  if (!prevStart.date && !prevEnd.date) {
    return {
      start: start.setMovable(true),
      end: nullDate,
    };
  }

  // only one of prev date is valid
  if (Boolean(prevStart.date) !== Boolean(prevEnd.date)) {
    if (prevStart.date) {
      if (prevStart.time !== start.time) {
        start.setMovable(true);
      }
      return { start, end: nullDate, reverse };
    }

    if (prevEnd.time !== end.time) {
      end.setMovable(true);
    }
    return { start: nullDate, end, reverse };
  }

  // all prev dates are valid
  if (prevStart.time === start.time) {
    return { start: start.setMovable(true), end: nullDate, reverse };
  }

  return { start: nullDate, end, reverse };
};
